/**
 * State management for SDK
 */

import type { AlterError } from "../types";

/**
 * SDK internal state
 */
export interface SDKState {
  isOpen: boolean;
  sessionToken: string | null;
  error: AlterError | null;
}

type StateListener = (state: SDKState) => void;

/**
 * Simple state manager
 */
export class StateManager {
  private state: SDKState;
  private listeners: Set<StateListener>;

  constructor() {
    this.state = {
      isOpen: false,
      sessionToken: null,
      error: null,
    };
    this.listeners = new Set();
  }

  /**
   * Get current state
   */
  getState(): Readonly<SDKState> {
    return { ...this.state };
  }

  /**
   * Get a specific state value
   */
  get<K extends keyof SDKState>(key: K): SDKState[K] {
    return this.state[key];
  }

  /**
   * Update state
   */
  setState(updates: Partial<SDKState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Clear all listeners
   */
  clearListeners(): void {
    this.listeners.clear();
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const currentState = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(currentState);
      } catch (error) {
        console.error("[Alter Connect] Error in state listener:", error);
      }
    });
  }
}
